import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from 'sweetalert2';
import { toast } from "react-toastify";
import { AiOutlineCheckCircle, AiOutlineLoading3Quarters } from 'react-icons/ai';
import { IoMdCloseCircle } from 'react-icons/io';
import CryptoJS from "crypto-js";
import { Thumbmark } from '@thumbmarkjs/thumbmarkjs';
import FingerprintOSS from '@fingerprintjs/fingerprintjs';

const FP_CACHE_KEY = "fp";

const getCachedFingerprint = () => {
  try {
    return sessionStorage.getItem(FP_CACHE_KEY) || "";
  } catch (error) {
    return "";
  }
};

const setCachedFingerprint = (value) => {
  if (!value) return;
  try {
    sessionStorage.setItem(FP_CACHE_KEY, value);
  } catch (error) {
    // Ignore storage errors
  }
};

// Tạo component CSKHNote để tái sử dụng
const CSKHNote = () => (
  <div className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-600 italic px-1">
    * Vui lòng {' '}
    <a 
      href="https://llwin-cskh.pages.dev/" 
      target="_blank" 
      rel="noopener noreferrer" 
      className="text-blue-600 hover:text-blue-800 underline"
    >
      liên hệ CSKH
    </a>
    {' '}nếu cần thêm thông tin
  </div>
);

const ListPromotionModal = ({ isOpen, onClose, promotions, username }) => {
  const [receivedPromotions, setReceivedPromotions] = useState(new Set());
  const [submittedPromotions, setSubmittedPromotions] = useState(new Set());
  const [loadingPromotions, setLoadingPromotions] = useState(new Set());
  const [failedPromotions, setFailedPromotions] = useState(new Set());
  const [depositClaimed, setDepositClaimed] = useState(false); // Theo dõi trạng thái nhận khuyến mãi deposit
  const manualPromoCodes = ["BH03", "ND188", "ND01", "APP108"];
  const [smsInfo, setSmsInfo] = useState({ phoneNumber: '', smsCode: '', promoCode: '' });
  const [showSMSGuide, setShowSMSGuide] = useState(false); // Thêm state mới

  const [orderNo, setOrderNo] = useState("");
  const [showOrderNo, setShowOrderNo] = useState(false);
  const [isSubmittingOrderNo, setIsSubmittingOrderNo] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState({
    code: "",
    category: "",
  });
  const FINGERPRINT_REQUIRED_PROMOS = new Set(['ND188', 'ND01', 'ND02', 'ND03', 'DK58', 'USDT-ND']);
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    if (!isOpen) {
      setReceivedPromotions(new Set());
      setSubmittedPromotions(new Set());
      setDepositClaimed(false);
      setSmsInfo({ phoneNumber: '', smsCode: '', promoCode: '' });
      setShowSMSGuide(false);
      setFailedPromotions(new Set());
      setShowOrderNo(false);
      setOrderNo("");
      setSelectedPromo({ code: "", category: "" });
      document.body.style.overflow = 'auto';
    } else {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const handleApplyPromotion = async (promoCode, orderNo = "") => {
    setLoadingPromotions(prev => new Set(prev).add(promoCode));
    setFailedPromotions(prev => {
      const newSet = new Set(prev);
      newSet.delete(promoCode);
      return newSet;
    });

    try {
      let fingerprint;
      // Lấy fingerprint cho các khuyến mãi yêu cầu xác thực
      if (FINGERPRINT_REQUIRED_PROMOS.has(promoCode)) {
        try {
          // Ưu tiên lấy từ cache (sessionStorage)
          fingerprint = getCachedFingerprint();
          if (fingerprint) {
            console.log("[Fingerprint] Cache hit", {
              promoCode,
              hasVisitorId: Boolean(fingerprint),
            });
          }

          // Thumbmark trước, retry 3 lần mỗi lần cách 1s
          if (!fingerprint) {
            const thumbmarkApiKey = (process.env.REACT_APP_THUMBMARK_API_KEY || "").trim();
            let lastThumbmarkError;
            for (let attempt = 1; attempt <= 3; attempt += 1) {
              try {
                const tm = new Thumbmark({
                  ...(thumbmarkApiKey && { api_key: thumbmarkApiKey }),
                });
                const result = await tm.get();
                // Có API key ưu tiên visitorId; không có thì dùng thumbmark
                fingerprint = result?.visitorId || result?.thumbmark || "";
                console.log("[Thumbmark] Success", {
                  promoCode,
                  hasApiKey: Boolean(thumbmarkApiKey),
                  hasVisitorId: Boolean(fingerprint),
                  attempt,
                });
                break;
              } catch (attemptError) {
                lastThumbmarkError = attemptError;
                console.warn("[Thumbmark] Attempt failed", {
                  promoCode,
                  attempt,
                  message: attemptError?.message,
                });
                if (attempt < 3) {
                  await sleep(1000);
                }
              }
            }

            // Fallback Fingerprint free (OSS) nếu Thumbmark lỗi
            if (!fingerprint) {
              console.warn("[Thumbmark] Failed, fallback to Fingerprint OSS", {
                promoCode,
                message: lastThumbmarkError?.message,
              });
              const fpFallback = await FingerprintOSS.load();
              const fallbackResult = await fpFallback.get();
              fingerprint = fallbackResult.visitorId;
              console.log("[FingerprintJS] OSS success", {
                promoCode,
                hasVisitorId: Boolean(fingerprint),
              });
            }

            setCachedFingerprint(fingerprint);
          }

          if (!fingerprint) {
            toast.error("Không thể xác thực thiết bị. Vui lòng thử lại sau.");
            return false;
          }
        } catch (fpError) {
          console.error("Lỗi khi lấy Fingerprint:", fpError);
          toast.error("Không thể xác thực thiết bị. Vui lòng thử lại sau.");
          return false;
        }
      }

      const payload = {
        promotions: [promoCode],
        username: username.toLowerCase(),
        orderNo: orderNo,
      };

      const secretKey = 'RR882024RR88';
      const encryptedPayload = CryptoJS.AES.encrypt(JSON.stringify(payload), secretKey).toString();

      // Kiểm tra nếu là khuyến mãi yêu cầu xác thực và không có fingerprintTNTN
      if ((promoCode === 'ND188' || promoCode === 'ND01' || promoCode === 'DK58' ) && !fingerprint) {
        toast.error("Không thể xác thực thiết bị. Vui lòng thử lại sau.");
        return false;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/promotion/apply`,
        { data: encryptedPayload },
        {
          headers: {
            ...(fingerprint && { 'X-Fingerprint': fingerprint }),
          },
        }
      );

      if (response.data.statusCode === 200) {
        // Chỉ lấy thông tin SMS cho các khuyến mãi cần xác thực SMS
        // if (promoCode === 'DK58' || (promoCode === 'ND188' && response.data?.phoneNumber !== null)  || promoCode === 'KM100') {
        if (promoCode === 'DK58' || promoCode === 'ND188' || promoCode === 'KM100' || promoCode === 'ND01' || promoCode === 'NT50') {

          try {
            const smsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/promotion/code/${username}/${promoCode}`);
            if (smsResponse.data.statusCode === 200) {
              setSmsInfo({
                smsCode: smsResponse.data.data.smsCode,
                phoneNumber: smsResponse.data.data.phoneNumber,
                promoCode: promoCode
              });
              setShowSMSGuide(true);
            }
          } catch (smsError) {
            console.error("Lỗi khi lấy thông tin SMS:", smsError);
          }
        }

        toast.success(response.data.message || `Thành công!`);
        return true;
      } else {
        setFailedPromotions(prev => new Set(prev).add(promoCode));
        toast.error(response.data.message || `Lỗi khi nhận khuyến mãi ${promoCode}.`);
        return false;
      }
    } catch (error) {
      setFailedPromotions(prev => new Set(prev).add(promoCode));
      toast.error("Lỗi kết nối, vui lòng thử lại.");
      return false;
    } finally {
      setLoadingPromotions(prev => {
        const newSet = new Set(prev);
        newSet.delete(promoCode);
        return newSet;
      });
    }
  };
  
  const handleClaim = async (promoCode, category) => {
    try {
      const success = await handleApplyPromotion(promoCode);
      if (success) {
        setReceivedPromotions((prevState) => new Set(prevState.add(promoCode)));
        // Nếu khuyến mãi thuộc category "deposit", vô hiệu hóa các khuyến mãi cùng category
        if (category === "deposit") {
          setDepositClaimed(true);
        }
      }
    } catch (error) {
      // Xử lý lỗi nếu cần
    }
  };

  const handleApprovalSubmit = async (promoCode, category, orderNo = "") => {
    try {
      const success = await handleApplyPromotion(promoCode, orderNo);
      if (success) {
        setSubmittedPromotions((prev) => new Set(prev).add(promoCode));
        if (category === "deposit") {
          setDepositClaimed(true);
        }
      }
    } catch (error) {
      // Xử lý lỗi nếu cần
    }
  };

  // Thêm hàm kiểm tra xem có promotion nào đang loading không
  const isAnyPromotionLoading = loadingPromotions.size > 0;

  // Thêm hàm xử lý copy
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        toast.success('Đã sao chép vào clipboard');
      })
      .catch(() => {
        toast.error('Không thể sao chép. Vui lòng thử lại');
      });
  };

  // Thêm hàm để gọi API lấy thông tin SMS
  const getSMSInfo = async (username, promoCode) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/promotion/code/${username}/${promoCode}`);
      if (response.data.statusCode === 200) {  // Kiểm tra statusCode
        setSmsInfo({
          smsCode: response.data.data.smsCode,      // Lấy từ response.data.data
          phoneNumber: response.data.data.phoneNumber, // Lấy từ response.data.data
          promoCode: promoCode
        });
        setShowSMSGuide(true);
        toast.success(response.data.message || "Đã lấy thông tin SMS thành công!");
      }
    } catch (error) {
      toast.error("Không thể lấy thông tin SMS. Vui lòng thử lại sau.");
    }
  };

  const getStatusSMSInfo = async (username, promoCode) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/promotion/code/status/${username}/${promoCode}`
      );

      if (response.data.statusCode === 200) {
        const data = response.data.data;

        if (data.status === 'success') {
          Swal.fire({
            icon: 'success',
            title: 'Xác thực thành công',
            text: 'Bạn đã xác thực SMS thành công!',
            confirmButtonText: 'OK',
            confirmButtonColor: '#1678ff',
          });
        } else {
          Swal.fire({
            icon: 'warning',
            title: 'Chưa xác thực',
            text: data.message || 'Chưa xác thực SMS',
            confirmButtonText: 'Đóng',
            confirmButtonColor: '#ef4444',
          });
        }
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Có lỗi xảy ra',
        text: 'Không thể kiểm tra xác thực SMS. Vui lòng thử lại sau.',
        confirmButtonText: 'Đóng',
        confirmButtonColor: '#ef4444',
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-[110]" onClick={onClose}>
      <div 
        className="bg-white/60 backdrop-blur-sm p-4 md:p-6 rounded-xl shadow-2xl w-[95%] md:w-3/4 max-w-4xl relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4 border-b pb-2">
          <h2 className="text-xl font-bold text-[#1678ff]">Danh sách khuyến mãi</h2>
          <button
            className="text-gray-400 hover:text-gray-900 font-bold text-3xl p-1"
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        {/* Điều kiện hiển thị mới */}
        {showSMSGuide && smsInfo.phoneNumber && smsInfo.smsCode ? (
          <div className="p-2 sm:p-4">
            <h2 className="text-lg sm:text-xl font-bold text-center text-[#1678ff] mb-4 sm:mb-6">
              Thông Tin Xác Thực
            </h2>
            <div className=" p-3 sm:p-6">
              <div className="space-y-3 sm:space-y-4">
                {/* Bước 1 */}
                <div className="bg-white p-3 sm:p-4 rounded-lg border border-blue-100 shadow-sm">
                  <p className="text-gray-700 mb-2 text-sm sm:text-base">
                    <span className="font-medium">Bước 1:</span> Soạn tin nhắn với nội dung
                  </p>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-blue-50 p-2 sm:p-3 rounded text-center break-all">
                      <p className="text-blue-600 font-bold text-base sm:text-lg select-all">
                        {smsInfo.smsCode}
                      </p>
                    </div>
                    <button
                      onClick={() => handleCopy(smsInfo.smsCode)}
                      className="bg-blue-500 text-white p-2 rounded-md hover:bg-blue-600 
                      transition-colors flex items-center justify-center min-w-[40px] sm:min-w-[60px]"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                        <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Bước 2 */}
                <div className="bg-white p-3 sm:p-4 rounded-lg border border-blue-100 shadow-sm">
                  <p className="text-gray-700 mb-2 text-sm sm:text-base">
                    <span className="font-medium">Bước 2:</span> Gửi đến số điện thoại
                  </p>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-blue-50 p-2 sm:p-3 rounded text-center">
                      <p className="text-blue-600 font-bold text-base sm:text-lg select-all">
                        {smsInfo.phoneNumber}
                      </p>
                    </div>
                    <button
                      onClick={() => handleCopy(smsInfo.phoneNumber)}
                      className="bg-blue-500 text-white p-2 rounded-md hover:bg-blue-600 
                      transition-colors flex items-center justify-center min-w-[40px] sm:min-w-[60px]"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                        <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Nút gửi tin nhắn - Chỉ hiển thị trên mobile */}
                <div className="block sm:hidden">
                  <a
                    href={`sms:${smsInfo.phoneNumber}?&body=${smsInfo.smsCode}`}
                    className="w-full px-4 py-3 rounded-full text-white font-bold text-base
                    bg-gradient-to-b from-[#2f8cff] to-[#1d67e8] border-2 border-white
                    hover:from-[#3a95ff] hover:to-[#246ff0] transition-all duration-200
                    flex items-center justify-center active:scale-[0.98]
                    shadow-[0_6px_18px_rgba(37,99,235,0.35),inset_0_1px_0_rgba(255,255,255,0.35)]"
                  >
                    <div className="flex items-center justify-center space-x-2 relative z-10">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        className="h-5 w-5" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor"
                        strokeWidth="2.5" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                      <span className="tracking-wide">Gửi tin nhắn ngay</span>
                    </div>
                  </a>
                </div>

                <div className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-600 italic px-1">
                  * Vui lòng {' '}
                  <a 
                    href="" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    liên hệ CSKH
                  </a>
                  {' '}nếu cần thêm thông tin
                </div>
                <div className="text-center">
                  <button
                  onClick={() => getStatusSMSInfo(username, smsInfo.promoCode)}
                  className="text-red-600 hover:text-red-800 font-medium underline 
                  transition-colors duration-200 text-md"
                >
                  Kiểm tra thông tin xác thực
                </button>
                </div>
              </div>
            </div>
          </div>
        ) : showOrderNo ? (
          <div className="max-w-md mx-auto p-4">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nhập mã vé cược
              </label>

              <div className="flex space-x-2">
                <input
                  type="text"
                  value={orderNo}
                  onChange={(e) => setOrderNo(e.target.value)}
                  placeholder="Nhập mã Order..."
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm
                              focus:outline-none focus:ring-2 focus:ring-[#61f1fb]
                              focus:border-[#61f1fb]"
                />

                <button
                  onClick={async () => {
                    if (!orderNo.trim() || !selectedPromo.code) return;
                    setIsSubmittingOrderNo(true);
                    try {
                      await handleApprovalSubmit(
                        selectedPromo.code,
                        selectedPromo.category,
                        orderNo
                      );
                    } finally {
                      setIsSubmittingOrderNo(false);
                      onClose();
                    }
                  }}
                  disabled={!orderNo.trim() || isSubmittingOrderNo}
                  className={`px-4 py-2 rounded-md text-white text-sm font-semibold
                      bg-gradient-to-b from-[#61f1fb] via-[#61f1fb] to-[#2063fc]
                      hover:opacity-90 transition
                      ${!orderNo.trim() || isSubmittingOrderNo
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                    }
                    `}
                >
                  {isSubmittingOrderNo ? "Đang gửi..." : "Gửi"}
                </button>
              </div>

              <p className="mt-2 text-xs text-gray-500 italic">
                * Vui lòng nhập đúng mã vé cược Thua sát nút (8 điểm thua 9 điểm) hoặc Thắng sít sao (1 điểm thắng 0 điểm)
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {/* Kiểm tra nếu không có promotion nào */}
            {!promotions || promotions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8">
                <p className="text-center text-gray-600 font-medium text-lg">
                  Hiện tại không có khuyến mãi nào đủ điều kiện nhận, vui lòng thử lại sau.
                </p>
                <CSKHNote />
              </div>
            ) : (
              <>
                {/* Kiểm tra có khuyến mãi hợp lệ không */}
                {promotions.filter(promo => !promo.isRejected).length === 0 ? (
                  <div className="mt-6 bg-red-50 border border-red-100 rounded-lg p-4">
                    <h3 className="text-xl font-semibold text-red-600 mb-4">
                      Khuyến mãi không khả dụng
                    </h3>
                    <div className="space-y-3">
                      {promotions.filter(promo => promo.isRejected).map((promo) => (
                        <div 
                          key={promo.code} 
                          className="bg-white p-4 rounded-lg border border-red-100 shadow-sm"
                        >
                          <div className="flex flex-col space-y-2">
                            <div className="flex items-start">
                              <span className="text-gray-700 font-medium w-24">Mã KM:</span>
                              <span className="text-[#1678ff] font-semibold">{promo.code}</span>
                            </div>
                            <div className="flex items-start">
                              <span className="text-gray-700 font-medium w-24">Tên KM:</span>
                              <span className="text-[#1678ff]">{promo.name}</span>
                            </div>
                            <div className="flex items-start">
                              <span className="text-gray-700 font-medium w-24">Lý do:</span>
                              <span className="text-[#1678ff] flex-1">{promo.rejectReason}</span>
                            </div>
                            {/* Cập nhật phần nút */}
                            {promo.rejectReason?.includes("chưa gửi sms xác thực") && (
                              <div className="mt-3">
                                {!smsInfo.smsCode && (
                                  <button
                                    onClick={() => getSMSInfo(username, promo.code)}
                                    className="text-blue-600 hover:text-blue-800 font-medium underline 
                                    transition-colors duration-200 text-sm"
                                  >
                                    Lấy thông tin SMS
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    <table className="w-full border border-gray-200 rounded-lg shadow-lg table-fixed">
                      <thead>
                        <tr className="bg-gray-100 text-left">
                          <th className="p-3 border-b text-sm sm:text-base min-w-[120px]">Mã</th>
                          <th className="p-3 border-b text-sm sm:text-base min-w-[200px]">Tên Khuyến mãi</th>
                          <th className="p-3 border-b text-sm sm:text-base text-center min-w-[120px]">Điểm thưởng</th>
                          <th className="p-3 border-b text-sm sm:text-base text-center min-w-[120px]">Nhận thưởng</th>
                        </tr>
                      </thead>
                      <tbody>
                        {promotions.filter(promo => !promo.isRejected).map((promo) => (
                          <tr key={promo.code} className="border-b hover:bg-gray-100 transition text-[#1678ff] font-medium">
                            <td className="p-3 text-sm sm:text-base break-words">{promo.code}</td>
                            <td className="p-3 text-sm sm:text-base">{promo.name}</td>
                            <td className="p-3 text-sm sm:text-base text-center text-green-500 font-semibold ">
                                {promo.code === "CSN03" ? "" : promo.reward}
                            </td>
                            <td className="p-3 text-sm sm:text-base text-center">
                              {receivedPromotions.has(promo.code) ? (
                                <div className="flex items-center justify-center text-green-500">
                                  <AiOutlineCheckCircle className="mr-2 text-lg" /> Đã nhận
                                </div>
                              ) : submittedPromotions.has(promo.code) ? (
                                <div className="flex items-center justify-center text-yellow-500">
                                  <AiOutlineCheckCircle className="mr-2 text-lg" /> Đã gửi
                                </div>
                              ) : failedPromotions.has(promo.code) ? (
                                <div className="flex items-center justify-center text-red-500">
                                  <IoMdCloseCircle className="mr-2 text-lg" /> Không thành công
                                </div>
                              ) : (
                                <button
                                  onClick={async () => {
                                    if (promo.approval === "manual") {
                                      if (promo.code === "CSN03") {
                                        setSelectedPromo({
                                          code: promo.code,
                                          category: promo.category,
                                        });
                                        setShowOrderNo(true);
                                      } else {
                                        await handleApprovalSubmit(
                                          promo.code,
                                          promo.category
                                        );
                                      }                                    
                                    } else {
                                      await handleClaim(promo.code, promo.category);
                                    }
                                  }}
                                  className={`bg-blue-500 text-white px-3 py-1 rounded-md hover:bg-blue-600 transform md:translate-x-[62%] text-xs sm:text-sm ${
                                    (promo.category === "deposit" && depositClaimed) || 
                                    (isAnyPromotionLoading && !loadingPromotions.has(promo.code)) ? 
                                    "opacity-50 cursor-not-allowed bg-gray-400" : ""
                                  } flex items-center justify-center min-w-[80px]`}
                                  disabled={
                                    (promo.category === "deposit" && depositClaimed) || 
                                    loadingPromotions.has(promo.code) ||
                                    (isAnyPromotionLoading && !loadingPromotions.has(promo.code))
                                  }
                                >
                                  {loadingPromotions.has(promo.code) ? (
                                    <div className="flex items-center justify-center">
                                      <AiOutlineLoading3Quarters className="animate-spin mr-2" />
                                      <span>Đang xử lý...</span>
                                    </div>
                                  ) : (
                                    promo.approval === "manual" ? "Đăng ký" : "Nhận"
                                  )}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Vẫn hiển thị khuyến mãi bị từ chối nếu có */}
                    {promotions.some(promo => promo.isRejected) && (
                      <div className="mt-6 bg-red-50 border border-red-100 rounded-lg p-4">
                        <h3 className="text-xl font-semibold text-red-600 mb-4">
                          Khuyến mãi không khả dụng
                        </h3>
                        <div className="space-y-3">
                          {promotions.filter(promo => promo.isRejected).map((promo) => (
                            <div 
                              key={promo.code} 
                              className="bg-white p-4 rounded-lg border border-red-100 shadow-sm"
                            >
                              <div className="flex flex-col space-y-2">
                                <div className="flex items-start">
                                  <span className="text-gray-700 font-medium w-24">Mã KM:</span>
                                  <span className="text-[#1678ff] font-semibold">{promo.code}</span>
                                </div>
                                <div className="flex items-start">
                                  <span className="text-gray-700 font-medium w-24">Tên KM:</span>
                                  <span className="text-[#1678ff]">{promo.name}</span>
                                </div>
                                <div className="flex items-start">
                                  <span className="text-gray-700 font-medium w-24">Lý do:</span>
                                  <span className="text-[#1678ff] flex-1">{promo.rejectReason}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Hiển thị lưu ý về ND188 và ND01 */}
                    {promotions.some((promo) => promo.code === "ND188" && !promo.isRejected) &&
                      promotions.some((promo) => promo.code === "ND01" && !promo.isRejected) && (
                        <div className="mt-4">
                          <p className="text-red-500 text-sm font-semibold">
                            * Khuyến mãi nạp đầu chỉ được nhận một trong hai khuyến mãi.
                          </p>
                        </div>
                    )}

                    {/* Thêm CSKHNote một lần duy nhất ở cuối */}
                    <div className="mt-4">
                      <CSKHNote />
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ListPromotionModal;
